import { OTHERS_KEY } from './chartColors';

/**
 * Agrupa la cola de categorías en un único "Otros".
 *
 * <b>Por qué hace falta.</b> Una dona con doce segmentos no comunica una
 * distribución: comunica que hay doce cosas. Los segmentos pequeños quedan por
 * debajo del ancho de un trazo, la leyenda ocupa más que el gráfico y ningún color
 * alcanza para distinguirlos. Lo mismo pasa con un ranking de veinte barras, donde
 * las últimas quince son ruido bajo la línea de lo legible.
 *
 * Agrupar la cola es lo que convierte "hay muchas categorías" en "estas mandan, el
 * resto suma esto".
 *
 * <b>Preserva el total.</b> "Otros" es la SUMA de lo que absorbe, no un descarte:
 * si el usuario compara la dona contra un KPI del mismo dato, los dos tienen que
 * dar la misma cifra.
 *
 * <b>Ordena SIEMPRE, no solo cuando hay cola que agrupar.</b> Antes había un atajo que devolvía
 * las filas tal cual si no superaban el límite, así que el criterio de orden del gráfico cambiaba
 * al cruzarlo: un ranking de cinco categorías salía en el orden en que lo devolviera la consulta
 * y el de ocho, ordenado. Un ranking desordenado no es un ranking.
 *
 * ⚠️ Por eso mismo, NO usar en gráficos de dimensión temporal: ordenar por valor una serie de
 * tiempo destruye justamente lo que la serie cuenta (ver `ChartVentasMensual`).
 */
export function groupTail<T extends Record<string, unknown>>(
  rows: T[],
  categoryField: string,
  valueField: string,
  limit: number,
): Record<string, unknown>[] {
  const toNumber = (value: unknown): number => (typeof value === 'number' ? value : Number(value) || 0);

  // `[...rows]` antes de ordenar: `sort` es in-place y estas filas vienen de la caché de
  // TanStack Query, que otros consumidores comparten.
  const sorted = [...rows].sort((a, b) => toNumber(b[valueField]) - toNumber(a[valueField]));
  if (sorted.length <= limit) return sorted as Record<string, unknown>[];

  const head = sorted.slice(0, limit);
  const tail = sorted.slice(limit);

  const tailTotal = tail.reduce((sum, row) => sum + toNumber(row[valueField]), 0);

  return [
    ...(head as Record<string, unknown>[]),
    {
      [categoryField]: OTHERS_KEY,
      [valueField]: tailTotal,
      /** Cuántas categorías absorbió. El tooltip lo dice: "Otros (7 categorías)". */
      __groupedCount: tail.length,
    },
  ];
}

/** Si una fila es el grupo de cola. */
export function isOthersRow(value: unknown): boolean {
  return value === OTHERS_KEY;
}
